import React, { useState, useEffect, useRef } from 'react'
import { Input, Upload, message } from 'antd'
import { FileImageOutlined } from '@ant-design/icons'
import { UploadRequestOption } from 'rc-upload/lib/interface'
import { UploadFile, UploadChangeParam } from 'antd/lib/upload/interface.d'
import { PlusOutlined } from '@ant-design/icons'
import { compact } from 'lodash'

import { formatErrors } from 'Utils'
import { uploadProductImage } from 'Services/products'
import './DynamicContent.scss'
import { useStoreActions, useStoreState } from 'easy-peasy'

export type ImageProps = {
  onChange: (images: string[] | string | undefined) => void
  value: string[] | undefined
  imageCount?: number
  defaultFileList?: string[] | any
  fieldName?: string
}

const ImageField: React.FC<ImageProps> = ({
  onChange: setImages,
  value,
  imageCount = 1,
  defaultFileList,
  fieldName,
}) => {
  const { selected_dynamic_param, content } = useStoreState((state) => state.campaignModule)
  const { setState } = useStoreActions((state: any) => state.campaignModule)
  const images = Array.isArray(value) ? value : value ? [value] : undefined
  const [inputHasValue, setInputHasValue] = useState(false)
  const firstRun = useRef(true)
  const [fileList, setFileList] = useState<UploadFile[]>(
    images?.length
      ? images?.map((image, index) => ({
          uid: index.toString(),
          name: 'image.png',
          status: 'done',
          url: image,
        }))
      : []
  )

  useEffect(() => {
    if (value) {
      setFileList([
        {
          uid: '0',
          name: 'image.png',
          status: 'done',
          url: Array.isArray(value) ? value[0] : value,
        },
      ])
    }
  }, [value])

  const handleUploadImage = async (info: UploadRequestOption) => {
    try {
      const file: any = info.file
      if (!file?.type.includes('image/')) {
        throw new Error('Only image files are allowed')
      }

      const resp = await uploadProductImage({
        image: info.file,
        config: {
          onUploadProgress: (event) => {
            info.onProgress &&
              info.onProgress({ ...event, percent: (event.loaded / event.total) * 100 })
          },
        },
      })
      const xhr = new XMLHttpRequest()
      info.onSuccess && info.onSuccess(resp.data[0].url, xhr)
    } catch (err: any) {
      // console.log('err', err?.toJSON())
      err && message.error('Cannot upload image, try another one')
      info.onError &&
        info.onError({
          name: 'image',
          message: err.message || 'Cannot upload image, try another one',
        })
    }
  }

  const onChange = (info: UploadChangeParam) => {
    info.fileList.forEach((file: UploadFile) => {
      if (file.response) file.url = file.response
      if (file.status === 'error') file.thumbUrl = ''
    })
    setFileList(info.fileList)
  }

  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false
      return
    }

    if (imageCount === 1) {
      if (fileList.length && !fileList[0]?.url) return
      setImages(fileList[0]?.url)
      return
    }

    setImages(compact(fileList.map((file) => file.url)))
  }, [fileList, imageCount])

  const renderMeat = () => {
    if (fileList && fileList[0] && fileList[0].status === 'done')
      return (
        <Upload
          listType="picture-card"
          fileList={fileList}
          defaultFileList={fileList}
          customRequest={handleUploadImage}
          onChange={onChange}
        >
          {fileList.length < imageCount && (
            <div>
              <PlusOutlined />
              <div className="mt-2">Upload</div>
            </div>
          )}
        </Upload>
      )
    else
      return (
        <Upload.Dragger fileList={fileList} customRequest={handleUploadImage} onChange={onChange}>
          <p className="ant-upload-drag-icon">
            <FileImageOutlined />
          </p>
          <p className="ant-upload-text">
            Click or drag file to this area to upload or Input below
          </p>
        </Upload.Dragger>
      )
  }

  return (
    <div className="CampaignEmail-ImageUpload">
      <div style={{ display: inputHasValue ? 'none' : 'block' }}>{renderMeat()} </div>
      <div>
        <Input
          placeholder="Enter image URL"
          value={content?.[fieldName as string] || value || ''}
          onChange={(e) => {
            setImages(e.target.value as any)
            setInputHasValue(!!e.target.value)
            setState({
              key: 'selected_dynamic_param',
              value: { key: fieldName, value: e.target.value, position: e?.target?.selectionStart },
            })
          }}
          onClick={(e) => {
            setState({
              key: 'selected_dynamic_param',
              value: {
                ...selected_dynamic_param,
                position: e.currentTarget.selectionStart,
              },
            })
          }}
          onFocus={(e) => {
            setState({
              key: 'selected_dynamic_param',
              value: {
                key: fieldName,
                value: e.currentTarget.value,
                position: e.currentTarget.selectionStart,
              },
            })
          }}
        ></Input>
      </div>
    </div>
  )
}

export default ImageField
